import { useEffect, useRef } from 'react'
import { cable, InboxMessage, ConversationMessage } from '@/lib/cable'
import type { Subscription } from '@rails/actioncable'

/**
 * Hook to subscribe to the InboxChannel for account-wide updates
 */
export function useInboxChannel(
  accountId: string | undefined,
  onMessage: (data: InboxMessage) => void
) {
  const subscriptionRef = useRef<Subscription | null>(null)
  const onMessageRef = useRef(onMessage)

  // Keep callback ref updated
  useEffect(() => {
    onMessageRef.current = onMessage
  }, [onMessage])

  useEffect(() => {
    if (!accountId) return

    // Subscribe to the inbox channel
    subscriptionRef.current = cable.subscriptions.create(
      { channel: 'InboxChannel', account_id: accountId },
      {
        received(data: InboxMessage) {
          onMessageRef.current(data)
        },
        connected() {
          console.log('[Cable] Connected to InboxChannel')
        },
        disconnected() {
          console.log('[Cable] Disconnected from InboxChannel')
        },
      }
    )

    return () => {
      subscriptionRef.current?.unsubscribe()
      subscriptionRef.current = null
    }
  }, [accountId])

  return subscriptionRef.current
}

/**
 * Hook to subscribe to the ConversationChannel for message updates
 */
export function useConversationChannel(
  conversationId: string | undefined,
  onMessage: (data: ConversationMessage) => void
) {
  const subscriptionRef = useRef<Subscription | null>(null)
  const onMessageRef = useRef(onMessage)

  // Keep callback ref updated
  useEffect(() => {
    onMessageRef.current = onMessage
  }, [onMessage])

  useEffect(() => {
    if (!conversationId) return

    // Subscribe to the conversation channel
    subscriptionRef.current = cable.subscriptions.create(
      { channel: 'ConversationChannel', conversation_id: conversationId },
      {
        received(data: ConversationMessage) {
          onMessageRef.current(data)
        },
        connected() {
          console.log('[Cable] Connected to ConversationChannel', conversationId)
        },
        disconnected() {
          console.log('[Cable] Disconnected from ConversationChannel')
        },
      }
    )

    return () => {
      subscriptionRef.current?.unsubscribe()
      subscriptionRef.current = null
    }
  }, [conversationId])

  return subscriptionRef.current
}
